import { Flex } from '@chilipiper/design-system'
import { Button } from '@chilipiper/design-system/src/new'
import { createValibotValidateFinalFormValues } from '@chilipiper/utils'
import React, { useMemo } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { logRocketIdentify } from '@chilipiper/tracking'
import { SubmitFormEntry } from '@chilipiper/api-client/src/chat-platform-chat'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { FillFormBlock as FillFormBlockType } from '../../../chat-data/types'
import { FormField } from '../form-field/FormField'
import { createValibotSchema } from '../form-field/validation'
import { parseValuesBeforeSubmitting, removeDotsFromDataFieldPath } from '../helpers'
import { useTranslations } from '../../../context/TranslationContext'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { useFillFormBlock } from './hooks'

interface Props {
  block: FillFormBlockType
}

export const FillFormBlock = ({ block }: Props) => {
  const submitted = Boolean(block.values)
  const { submitForm, isPreview, isSessionExpired } = useMessageFeedContext()
  const formFields = useFillFormBlock(block)
  const [sendFormField] = useTranslations(['sendFormField'])

  const mappedFields = useMemo(
    () =>
      block.fields.reduce(
        (dataFieldMap, dataField) => ({ ...dataFieldMap, [dataField.ref]: dataField }),
        {} as Record<string, SubmitFormEntry>
      ),
    [block.fields]
  )

  const validateForm = useMemo(
    () => createValibotValidateFinalFormValues(createValibotSchema(block.fields)),
    [block.fields]
  )
  const onSave = async (values: Partial<Record<string, string>>) => {
    const parsedValues = parseValuesBeforeSubmitting(values, mappedFields)

    if (parsedValues['PersonEmail']?.value) {
      logRocketIdentify({
        id: parsedValues['PersonEmail'].value.toString(),
        email: parsedValues['PersonEmail'].value.toString(),
      })
    }
    window.dispatchEvent(
      new CustomEvent(chiliChatEventName, {
        detail: { action: chatPublicEventAction.DATA_CAPTURED, args: { formData: parsedValues } },
      })
    )
    await submitForm(parsedValues)
  }

  const checkForEmptyFields = (values: Partial<Record<string, string>>) =>
    block.fields.some(
      formField => formField?.ref && !values[removeDotsFromDataFieldPath(formField.ref)]
    )

  return (
    <FinalForm
      onSubmit={onSave}
      validate={validateForm}
      render={({ handleSubmit, valid, values, submitting, submitSucceeded }) => {
        const hasEmptyField = checkForEmptyFields(values)
        return (
          <Flex
            as='form'
            onSubmit={handleSubmit}
            data-test-id='FillFormBlock'
            flexDirection='column'
            gap={4}
            p={5}
            bg='bg/section'
            boxShadow='border/section'
            borderRadius={16}
            w='100%'
          >
            {formFields.map((dataField, index) => {
              return (
                <FormField
                  key={dataField.ref}
                  dataField={dataField}
                  isDisabled={submitted || isSessionExpired || submitting || submitSucceeded}
                  autoFocus={index === 0}
                  submittedValue={block.values?.[dataField.ref]?.value}
                />
              )
            })}

            {!submitted && (
              <Button
                mt={1}
                isFullWidth
                variant='major'
                data-test-id='FillFormBlock-submitButton'
                isDisabled={(!isPreview && (hasEmptyField || !valid)) || isSessionExpired}
                isLoading={!isPreview && (submitting || submitSucceeded)}
                type='submit'
              >
                {sendFormField ?? 'Send'}
              </Button>
            )}
          </Flex>
        )
      }}
    />
  )
}
